import { CommandResponseMode, type DeviceCommand } from "./types";

const COMMAND_TIMEOUT_MS = 3000;
const MAX_COMMAND_RETRIES = 2;

/**
 * Builds one scanner command with the response mode and retry policy the protocol expects.
 * Keeping these defaults in one place makes it easier to change timing without touching every caller.
 */
function createCommand(
  name: string,
  payload: string,
  responseMode: DeviceCommand["responseMode"],
  {
    timeoutMs = COMMAND_TIMEOUT_MS,
    maxRetries = MAX_COMMAND_RETRIES,
    retryOnBusy = true,
  }: Partial<
    Pick<DeviceCommand, "timeoutMs" | "maxRetries" | "retryOnBusy">
  > = {},
): DeviceCommand {
  return {
    name,
    payload,
    responseMode,
    timeoutMs,
    maxRetries,
    retryOnBusy,
  };
}

/**
 * Canonical TruTest payloads used by the XRS2i protocol.
 * These strings are kept explicit because each one maps to a concrete scanner prompt or acknowledgement.
 */
export const XRS2I_COMMAND_PAYLOAD = {
  ENABLE_ACKS: "{ZA1}",
  ENABLE_ERRORS: "{ZE1}",
  QUERY_EID_OUTPUT_FORMAT: "{FS}",
  SELECT_SESSION_DATA_MODE: "{FGDD}",
  GET_SESSION_COUNT: "{FLN}",
  GET_NEXT_SESSION_ID: "{FL}",
  RESET_SESSION_RECORD_CURSOR: "{FD}",
  GET_NEXT_FIELD_CODE: "{FH}",
  GET_NEXT_RECORD: "{FN}",
} as const;

/**
 * Requests a specific saved session from the scanner's session list.
 * The command waits for an ACK because the device only advances to the selected session after acknowledging the request.
 */
export function buildSelectSessionCommand(sessionId: string): DeviceCommand {
  return createCommand(
    "selectSession",
    `{FF${sessionId}}`,
    CommandResponseMode.ACK,
  );
}

/**
 * Requests the user-visible name for one saved session.
 * The protocol treats the response as data because the scanner returns the label, not an acknowledgement.
 */
export function buildSessionNameCommand(sessionId: string): DeviceCommand {
  return createCommand(
    "getSessionName",
    `{FPNA${sessionId}}`,
    CommandResponseMode.DATA,
  );
}

function requestEidOutputFormatCommand(): DeviceCommand {
  // Ask the scanner how it formats live EIDs so later payload parsing matches the device's own output style.
  return createCommand(
    "requestEidOutputFormat",
    XRS2I_COMMAND_PAYLOAD.QUERY_EID_OUTPUT_FORMAT,
    CommandResponseMode.DATA,
  );
}

/**
 * Turns on ACKs and device error reporting before the driver asks for live scan format data.
 * Those settings make the scanner's later responses predictable enough for the protocol parser to follow.
 */
export function syncHardwareConfigCommands(): DeviceCommand[] {
  return [
    createCommand(
      "enableAcks",
      XRS2I_COMMAND_PAYLOAD.ENABLE_ACKS,
      CommandResponseMode.ACK,
      { maxRetries: 0 },
    ),
    createCommand(
      "enableErrors",
      XRS2I_COMMAND_PAYLOAD.ENABLE_ERRORS,
      CommandResponseMode.ACK,
      { maxRetries: 0 },
    ),
    requestEidOutputFormatCommand(),
  ];
}

/**
 * Resets the record cursor before the saved-session import starts.
 * This puts the scanner at the first record of the current session so the import walks data in order.
 */
export function resetSessionRecordCursorCommand(): DeviceCommand {
  return createCommand(
    "resetSessionRecordCursor",
    XRS2I_COMMAND_PAYLOAD.RESET_SESSION_RECORD_CURSOR,
    CommandResponseMode.ACK,
  );
}

/**
 * Switches the scanner into the mode that exposes saved session data.
 * The driver must request that mode before it can enumerate sessions, names, and records.
 */
export function selectSessionDataModeCommand(): DeviceCommand {
  return createCommand(
    "selectSessionDataMode",
    XRS2I_COMMAND_PAYLOAD.SELECT_SESSION_DATA_MODE,
    CommandResponseMode.ACK,
  );
}

/**
 * Requests the number of saved sessions present on the scanner.
 * That count drives progress reporting and lets the import detect the empty-session case early.
 */
export function getSessionCountCommand(): DeviceCommand {
  return createCommand(
    "getSessionCount",
    XRS2I_COMMAND_PAYLOAD.GET_SESSION_COUNT,
    CommandResponseMode.DATA,
  );
}

/**
 * Requests the next saved session identifier from the scanner.
 * The import loop keeps calling this until the device returns an empty value that marks the end of the list.
 */
export function getNextSessionIdCommand(): DeviceCommand {
  return createCommand(
    "getNextSessionId",
    XRS2I_COMMAND_PAYLOAD.GET_NEXT_SESSION_ID,
    CommandResponseMode.DATA,
  );
}

/**
 * Requests the next field code while the scanner is still listing record metadata.
 * This keeps the import aligned with the scanner's record structure before actual scan rows begin.
 */
export function getNextFieldCodeCommand(): DeviceCommand {
  return createCommand(
    "getNextFieldCode",
    XRS2I_COMMAND_PAYLOAD.GET_NEXT_FIELD_CODE,
    CommandResponseMode.DATA,
  );
}

/**
 * Requests the next record in the current saved session.
 * The empty record response is the end-of-session marker that tells the import to move forward.
 */
export function getNextRecordCommand(): DeviceCommand {
  return createCommand(
    "getNextRecord",
    XRS2I_COMMAND_PAYLOAD.GET_NEXT_RECORD,
    CommandResponseMode.DATA,
  );
}
